import { type Maybe } from "@shopify/hydrogen-react/storefront-api-types";
import useSWR from "swr/immutable";

import {
  AVAILABLE_MARKETS,
  type MarketIsoCode,
} from "@root/tablescape/constants";
import { isMarketIsoCode } from "@root/tablescape/utilities/markets";

export type GeolocationSuggestion =
  | {
      currentCountry: {
        name: string;
      };
      customerLocationMatchesStore: boolean;
      detectedCountry: {
        handle: Maybe<MarketIsoCode>;
        name: string;
      };
      isLoading: false;
    }
  | {
      currentCountry: undefined;
      customerLocationMatchesStore: boolean;
      detectedCountry: undefined;
      isLoading: boolean;
    };

type RawSuggestions = {
  detected_values?: {
    country?: {
      handle?: string;
      name?: string;
    };
  };
  features?: Record<string, unknown>;
  suggestions?: unknown[];
};

export const useGeolocation = (): GeolocationSuggestion => {
  const root = String(
    typeof window !== "undefined" ? window.Shopify?.routes?.root ?? "/" : "/"
  );
  const currentCountry = String(
    typeof window !== "undefined" ? window.Shopify?.country ?? "" : ""
  );

  const { data, error, isLoading } = useSWR<RawSuggestions>(
    ["geolocation-suggestions", currentCountry, root],
    ([, exclude, base]) =>
      fetch(
        String(base) +
          "browsing_context_suggestions.json?country[enabled]=true&country[exclude]=" +
          String(exclude)
      ).then((r) => r.json())
  );

  if (isLoading === true) {
    return {
      currentCountry: undefined,
      detectedCountry: undefined,
      customerLocationMatchesStore: false,
      isLoading,
    };
  }
  if (!data?.detected_values?.country || error) {
    return {
      currentCountry: undefined,
      detectedCountry: undefined,
      customerLocationMatchesStore: false,
      isLoading,
    };
  }

  const rawHandle = data.detected_values.country.handle;
  const handle: Maybe<MarketIsoCode> = isMarketIsoCode(rawHandle)
    ? rawHandle
    : null;

  const countryName = handle
    ? AVAILABLE_MARKETS[handle].country
    : data.detected_values.country.name ?? "";

  const customerLocationMatchesStore =
    !!handle && handle.toUpperCase() === currentCountry.toUpperCase();

  return {
    currentCountry: {
      name: currentCountry,
    },
    detectedCountry: {
      handle,
      name: countryName,
    },
    customerLocationMatchesStore,
    isLoading,
  };
};
